import { Dispatch } from '@reduxjs/toolkit';
import {
  clearReadPaymentData,
  markPaymentDataRead,
  setNotificationsData,
  setPaymentDoneModalData,
  setShowPaymentDoneModal,
  showNotificationStickyBanner
} from '../../reducers/notificationsSlice';
import axiosInstance, { ApiKeys, getApiUrl, logError } from '../../utils/ApiHelper';
import {
  pushToSessionStorage,
  removeSessionStorageByKey,
  getFromSessionStorage
} from '../../utils/StorageUtils';
import { AxiosError } from 'axios';
import { ActionsRequestPayload, PaymentDoneData } from './interfaces';
import { getLeaderboardDetails } from '@cp/src/pages/auth/AuthActions';
import store from '@cp/src/store';
import { getServerTime } from '@cp/src/utils/DateHelper';
import { fullMonthNames } from '@cp/src/constants/Common.constants';
import { buildNotificationTopic } from './utils';
import { LimitAlerts, SUBSCRIPTION_CASE_ID_KEY, SUBSCRIPTION_DATA_KEY } from './constants';
import { API_STATUS_CODE } from '@cp/src/utils/ApiHelperUtils';
import { subscriptionManager } from '@cp/src/utils/subscriptionManager';

interface dispatchProps {
  payload: any;
  type: string;
}
export const fetchNotifications = () => {
  const todaysDate = new Date();
  todaysDate.setDate(todaysDate.getDate() - 2);
  const url = getApiUrl(ApiKeys.FETCH_NOTIFICATIONS);
  return function (dispatch: ({ payload, type }: dispatchProps) => void) {
    axiosInstance
      .get(url, {
        params: { startTime: todaysDate.getTime() },
        headers: { includeInUptimeMetrics: true }
      })
      .then(response => {
        if (response) {
          dispatch(setNotificationsData(response.data));
        }
      });
  };
};

export const fetchNotificationsPolling = () => {
  const todaysDate = new Date();
  todaysDate.setDate(todaysDate.getDate() - 2);
  const url = getApiUrl(ApiKeys.FETCH_NOTIFICATIONS);
  return () =>
    axiosInstance.get(url, {
      params: { startTime: todaysDate.getTime() },
      headers: { includeInUptimeMetrics: true }
    });
};

export const triggerNotificationAction = (actionPayload: ActionsRequestPayload) => {
  const url = getApiUrl(ApiKeys.NOTIFICATION_ACTION);
  return function (dispatch: any) {
    axiosInstance
      .post(url, actionPayload, { headers: { includeInUptimeMetrics: true } })
      .then(response => {
        if (response) {
          dispatch(fetchNotifications());
        }
      })
      .catch(() => dispatch(fetchNotifications()));
  };
};

export const markNotificationAsRead = (actionPayload: ActionsRequestPayload) => {
  const url = getApiUrl(ApiKeys.NOTIFICATION_ACTION);
  return axiosInstance.post(url, actionPayload, { headers: { includeInUptimeMetrics: true } });
};

export const getStickyNotification = () => (dispatch: Dispatch) => {
  try {
    const url = getApiUrl(ApiKeys.STICKY_ALERT);
    axiosInstance.get(url, { headers: { includeInUptimeMetrics: true } }).then(response => {
      const data = response?.data;
      if (data) {
        dispatch(showNotificationStickyBanner(data));
      }
    });
  } catch (error) {
    logError(error, 'Error while fetching sticky notification');
  }
};

export const onPaymentDoneEventReceived = (payload: PaymentDoneData) => (dispatch: Dispatch) => {
  dispatch(setPaymentDoneModalData(payload));
};

export const triggerPaymentDoneModal =
  (payload: Array<string | undefined>) => (dispatch: Dispatch) => {
    dispatch(setShowPaymentDoneModal(true));
    dispatch(markPaymentDataRead(payload));
  };

export const closePaymentDoneModal = () => (dispatch: Dispatch) => {
  dispatch(setShowPaymentDoneModal(false));
  dispatch(clearReadPaymentData());
  const referenceId = store?.getState().common?.userData?.referenceId;
  const todaysDate = new Date(getServerTime());
  const currMonth = fullMonthNames[todaysDate?.getMonth() || 0];
  const currYear = todaysDate?.getFullYear();
  dispatch(getLeaderboardDetails(referenceId, currMonth, '', currYear));
};

const storeSubscriptionData = (caseReferenceId: string, data: any) => {
  try {
    pushToSessionStorage(SUBSCRIPTION_DATA_KEY, data);
    pushToSessionStorage(SUBSCRIPTION_CASE_ID_KEY, caseReferenceId);
  } catch (error) {
    logError(error, 'Failed to store subscription data in sessionStorage');
  }
};

const removeSubscriptionDataFromStorage = () => {
  try {
    removeSessionStorageByKey(SUBSCRIPTION_DATA_KEY);
    removeSessionStorageByKey(SUBSCRIPTION_CASE_ID_KEY);
  } catch (error) {
    logError(error, 'Failed to remove subscription data from sessionStorage');
  }
};

export const subscribeToCallAttemptAlerts =
  (businessVertical: string, caseReferenceId: string, isAlreadySubscribed: boolean) => async () => {
    try {
      const topic = buildNotificationTopic(
        businessVertical,
        caseReferenceId,
        LimitAlerts.CALL_ATTEMPT_LIMIT_ALERT
      );
      const url = getApiUrl(ApiKeys.SUBSCRIBE_NOTIFICATION_TOPIC, { topic });

      let response;
      if (isAlreadySubscribed) {
        response = await axiosInstance.get(url, {
          headers: { donotHandleError: true, includeInUptimeMetrics: true }
        });
      } else {
        response = await axiosInstance.put(
          url,
          {},
          { headers: { donotHandleError: true, includeInUptimeMetrics: true } }
        );
      }

      if (response?.data) {
        const subscriptionData = response.data;
        const expiresAt = subscriptionData.expiresAt;

        storeSubscriptionData(caseReferenceId, subscriptionData);

        if (expiresAt) {
          const handleExpiry = () => {
            removeSubscriptionDataFromStorage();
          };

          // addSubscription handles both new and existing subscriptions
          // It removes existing subscription before adding if one exists
          subscriptionManager.addSubscription(caseReferenceId, expiresAt, handleExpiry);
        }
      }
    } catch (error) {
      logError(error, 'Failed to subscribe to call attempt alerts');
    }
  };

export const unsubscribeFromCallAttemptAlerts = (caseReferenceId: string) => () => {
  subscriptionManager.removeSubscription(caseReferenceId);
  removeSubscriptionDataFromStorage();
};

export const getSubscriptionData = (): { caseId: string | null; data: any | null } => {
  try {
    const caseId = getFromSessionStorage(SUBSCRIPTION_CASE_ID_KEY);
    const data = getFromSessionStorage(SUBSCRIPTION_DATA_KEY);
    return { caseId: caseId || null, data: data || null };
  } catch (error) {
    logError(error, 'Failed to retrieve subscription data from sessionStorage');
    return { caseId: null, data: null };
  }
};
